import { countTopicOverlap } from '../topics/topic.utils';

/**
 * Soft ranking for Discover Circles (Suggestion Engine M1).
 * Applied within a fetched page when the viewer has topics; guests keep source order.
 *
 * score =
 *   topicOverlap × 5
 * + min(memberCount / 50, 5)
 * + recencyBoost(0..3)
 */
export type DiscoverCircleScoreInput = {
  viewerTopics: Set<string> | string[] | null | undefined;
  creatorTopics: string[] | null | undefined;
  memberCount?: number | null;
  publishedAt?: Date | string | null;
  now?: Date;
};

export function computeDiscoverCircleScore(input: DiscoverCircleScoreInput): number {
  const topicOverlap = countTopicOverlap(input.viewerTopics, input.creatorTopics);
  const memberBoost = Math.min(Math.max(input.memberCount ?? 0, 0) / 50, 5);
  const publishedAt =
    input.publishedAt instanceof Date
      ? input.publishedAt
      : input.publishedAt
        ? new Date(input.publishedAt)
        : null;
  const now = input.now ?? new Date();
  let recencyBoost = 0;
  if (publishedAt && !Number.isNaN(publishedAt.getTime())) {
    const ageDays = Math.max(0, (now.getTime() - publishedAt.getTime()) / (24 * 60 * 60 * 1000));
    if (ageDays <= 7) recencyBoost = 3;
    else if (ageDays <= 30) recencyBoost = 2;
    else if (ageDays <= 90) recencyBoost = 1;
  }
  return topicOverlap * 5 + memberBoost + recencyBoost;
}

export function sortByDiscoverCircleAffinity<T>(
  items: T[],
  getScoreInput: (item: T) => DiscoverCircleScoreInput,
): T[] {
  return [...items]
    .map((item, index) => ({ item, index, score: computeDiscoverCircleScore(getScoreInput(item)) }))
    .sort((left, right) => {
      if (right.score !== left.score) return right.score - left.score;
      return left.index - right.index;
    })
    .map((entry) => entry.item);
}
